import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import logoAsset from "@/assets/pixiweb-logo-white.png.asset.json";
import logoAvif from "@/assets/pixiweb-logo-white.avif.asset.json";
import logoWebp from "@/assets/pixiweb-logo-white.webp.asset.json";
import logoColorAsset from "@/assets/pixiweb-logo.png.asset.json";
import logoColorAvif from "@/assets/pixiweb-logo.avif.asset.json";
import logoColorWebp from "@/assets/pixiweb-logo.webp.asset.json";

const navLinks = [
  { href: "/#leistungen", label: "Leistungen", external: false },
  { href: "/#portfolio", label: "Portfolio", external: false },
  { href: "/ratgeber", label: "Ratgeber", external: true },
  { href: "/#faq", label: "FAQ", external: false },
  { href: "/#kontakt", label: "Kontakt", external: false },
] as const;

export function SiteHeader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
    <header
      className="pw-nav"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: scrolled ? "10px 32px" : "16px 32px",
        backdropFilter: "saturate(180%) blur(14px)",
        WebkitBackdropFilter: "saturate(180%) blur(14px)",
        background: scrolled ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.55)",
        borderBottom: scrolled ? "1px solid rgba(0,0,0,.05)" : "1px solid transparent",
        boxShadow: scrolled ? "0 8px 30px -16px rgba(20,40,90,.25)" : "none",
        transition: "background .3s,box-shadow .3s,border-color .3s,padding .3s",
      }}
    >
      <Link to="/" aria-label="PIXIWEB Startseite" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
        <picture>
          <source srcSet={logoColorAvif.url} type="image/avif" />
          <source srcSet={logoColorWebp.url} type="image/webp" />
          <img src={logoColorAsset.url} alt="PIXIWEB Webdesign Agentur" width={240} height={72} fetchPriority="high" decoding="async" style={{ height: scrolled ? 56 : 72, width: "auto", display: "block", transition: "height .3s" }} />
        </picture>
      </Link>

      <nav className="pw-navlinks" style={{ display: "flex", alignItems: "center", gap: 36 }}>
        {navLinks.map((item) =>
          item.external ? (
            <Link key={item.href} to={item.href} className="pw-link" style={{ textDecoration: "none", fontSize: 15, fontWeight: 500, color: "#3a414d" }}>
              {item.label}
            </Link>
          ) : (
            <a key={item.href} href={item.href} className="pw-link" style={{ textDecoration: "none", fontSize: 15, fontWeight: 500, color: "#3a414d" }}>
              {item.label}
            </a>
          )
        )}
        <a href="/#kontakt" className="pw-cta-btn" style={{ textDecoration: "none", fontSize: 15, fontWeight: 600, color: "#fff", background: "linear-gradient(135deg,#2e8bff,#5fc0ff)", padding: "11px 22px", borderRadius: 100 }}>
          Projekt anfragen
        </a>
      </nav>

      <button
        aria-label="Menü öffnen"
        className="pw-burger"
        onClick={() => setOpen(true)}
        style={{ display: "none", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 8 }}
      >
        <span style={{ width: 24, height: 2, background: "#0a0b10", borderRadius: 2 }} />
        <span style={{ width: 24, height: 2, background: "#0a0b10", borderRadius: 2 }} />
        <span style={{ width: 24, height: 2, background: "#0a0b10", borderRadius: 2 }} />
      </button>
    </header>

    {open && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "#ffffff",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 28,
          }}
        >
          <button
            aria-label="Menü schließen"
            onClick={() => setOpen(false)}
            style={{ position: "absolute", top: 24, right: 28, background: "none", border: "none", fontSize: 34, cursor: "pointer", color: "#0a0b10", lineHeight: 1 }}
          >
            ×
          </button>
          {navLinks.map((item) =>
            item.external ? (
              <Link key={item.href} to={item.href} onClick={() => setOpen(false)} style={{ textDecoration: "none", fontSize: 28, fontWeight: 600, color: "#0a0b10" }}>
                {item.label}
              </Link>
            ) : (
              <a key={item.href} href={item.href} onClick={() => setOpen(false)} style={{ textDecoration: "none", fontSize: 28, fontWeight: 600, color: "#0a0b10" }}>
                {item.label}
              </a>
            )
          )}
          <a
            href="/#kontakt"
            onClick={() => setOpen(false)}
            style={{ textDecoration: "none", fontSize: 18, fontWeight: 600, color: "#fff", background: "linear-gradient(135deg,#2e8bff,#5fc0ff)", padding: "14px 30px", borderRadius: 100, marginTop: 8 }}
          >
            Projekt anfragen
          </a>
        </div>
      )}
    </>
  );
}

const footerLinks = [
  { href: "/#leistungen", label: "Leistungen" },
  { href: "/#portfolio", label: "Portfolio" },
  { to: "/ratgeber", label: "Ratgeber" },
  { href: "/#faq", label: "FAQ" },
  { href: "/#kontakt", label: "Kontakt" },
  { to: "/impressum", label: "Impressum" },
  { to: "/datenschutz", label: "Datenschutz" },
] as const;

export function SiteFooter() {
  return (
    <footer className="pw-footer" style={{ background: "#0a0b10", color: "#fff", borderTop: "1px solid rgba(255,255,255,.08)" }}>
      <div className="pw-footer-inner" style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 32px", display: "flex", flexDirection: "column", gap: 24 }}>
        <Link to="/" aria-label="PIXIWEB Startseite" style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
          <picture>
            <source srcSet={logoAvif.url} type="image/avif" />
            <source srcSet={logoWebp.url} type="image/webp" />
            <img src={logoAsset.url} alt="PIXIWEB Webdesign Agentur" width={294} height={88} loading="lazy" decoding="async" style={{ height: 88, width: "auto", display: "block" }} />
          </picture>
        </Link>
        <nav style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
          {footerLinks.map((item) =>
            "to" in item ? (
              <Link key={item.to} to={item.to} className="pw-link" style={{ textDecoration: "none", color: "#9aa3b2", fontSize: 14 }}>
                {item.label}
              </Link>
            ) : (
              <a key={item.href} href={item.href} className="pw-link" style={{ textDecoration: "none", color: "#9aa3b2", fontSize: 14 }}>
                {item.label}
              </a>
            )
          )}
        </nav>
        <div style={{ color: "#5b6472", fontSize: 13 }}>
          © {new Date().getFullYear()} Pixiweb · Webdesign & SEO
        </div>
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,.1)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "14px 32px", display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("pixiweb:open-consent"))}
            style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,.6)", fontSize: 12, letterSpacing: ".15em", textTransform: "uppercase", fontFamily: "monospace" }}
          >
            Cookie-Einstellungen
          </button>
        </div>
      </div>
    </footer>
  );
}
